import { call, put, takeLatest, all } from "redux-saga/effects";
import request from "@util/net/request";
import axios, { AxiosResponse } from "axios";
import { loadOfficialAllStylingSaga } from "@redux/saga/allStyling/index";
import {
  GET_STYLING_OFFICIAL_LARGE_IMAGE_LIST,
  GET_STYLING_OFFICIAL_LIST,
  OfficialListActions
} from "@redux/reducer";
import {
  LargeOfficialStylingPayload,
  LargeOfficialStylingResponse,
  OfficialListPayload,
  OfficialListResponse,
  SBlargeImages,
  SBlargeImagesData,
  SBNormalImages,
  STYLING_SORT_BY,
  OfficialAllStylingInfo
} from "@type/index";

export function* loadOfficialListSaga(action: {
  payload: OfficialListPayload;
}) {
  const payload: OfficialListPayload = action.payload;
  try {
    yield put(OfficialListActions.setOfficialListErrorCode(""));
    yield put(OfficialListActions.getStylingOfficialListLoading(true));
    const res: AxiosResponse<OfficialListResponse> = yield call(
      request.get,
      "/styles/images/sb/normal",
      {
        params: { ...payload }
      }
    );
    if (res.data.status === "nok") {
      yield put(OfficialListActions.setOfficialListErrorCode("503"));
    } else {
      yield put(
        OfficialListActions.getStylingOfficialListSuccess(res.data.result)
      );
    }
  } catch (error) {
    if (!axios.isCancel(error)) {
      yield put(OfficialListActions.setOfficialListErrorCode("503"));
    }
  }
}

export function* loadOfficialLargeListSaga(action: {
  payload: LargeOfficialStylingPayload;
}) {
  try {
    const payload: LargeOfficialStylingPayload = action.payload;
    yield put(OfficialListActions.getOfficialLargeImageListLoading(true));
    const { gender_id, ...restPayload } = payload;
    const [officialRes, allStylingRes]: [
      AxiosResponse<LargeOfficialStylingResponse>,
      OfficialAllStylingInfo | undefined
    ] = yield all([
      call(request.get, "/styles/images/sb/large", {
        params: { ...restPayload }
      }),
      call(loadOfficialAllStylingSaga, {
        gender_id
      })
    ]);

    const officialStyles = officialRes.data.result.styles;

    // Only fetch the unfiltered (no filter, default sort) official list when
    // the UGC Admin image is not set and the large image list is non-empty
    // (otherwise the All Styling card is not rendered and the request is wasted).
    let fallbackImageUrl = "";
    if (officialStyles.length > 0 && !allStylingRes?.image_url) {
      try {
        const normalListRes: AxiosResponse<OfficialListResponse> = yield call(
          request.get,
          "/styles/images/sb/normal",
          {
            params: {
              departmentIds: payload.departmentIds,
              order: STYLING_SORT_BY.DISPLAY_START_AT_DESC,
              limit: 10,
              offset: 0
            }
          }
        );
        const normalStyles: Array<SBNormalImages> =
          normalListRes?.data?.result?.styles || [];
        // 10th outfit (0-based index 9); fall back to the last one when fewer
        // than 10 outfits are returned.
        const index = Math.min(9, normalStyles.length - 1);
        fallbackImageUrl = normalStyles[index]?.imageUrl || "";
      } catch {
        fallbackImageUrl = "";
      }
    }

    const allStyling: SBlargeImages = {
      featureId: "",
      imageUrl: allStylingRes?.image_url || fallbackImageUrl,
      title: "",
      description: ""
    };
    const list: SBlargeImagesData = {
      styles: officialStyles.length ? [allStyling, ...officialStyles] : []
    };
    yield put(OfficialListActions.getOfficialLargeImageListSuccess(list));
  } catch (error) {
    yield put(OfficialListActions.getOfficialLargeImageListLoading(false));
  }
}

export function* watchLoadOfficialListSaga() {
  yield takeLatest<any>(GET_STYLING_OFFICIAL_LIST, loadOfficialListSaga);
  yield takeLatest<any>(
    GET_STYLING_OFFICIAL_LARGE_IMAGE_LIST,
    loadOfficialLargeListSaga
  );
}
